import { useCallback, useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { URLS } from "../../Services/Constant";
import { get, post, put } from "../../Services/ApiHandler";
import { toast } from "react-toastify";
import moment from "moment";
import HttpClient from "../../Services/HttpClient";

const LOCATION_TYPES = {
  STATE: 2,
  CITY: 3,
  AREA: 4,
};

const CheckboxOption = (props) => {
  const isAllUsersActive = props.selectProps.isAllUsersSelected;
  const excludedIds = props.selectProps.excludedUserIds || [];
  const isAllGenericActive = props.selectProps.isAllSelected;

  let isChecked = props.isSelected;

  if (isAllUsersActive) {
    if (props.value === "all") {
      isChecked = excludedIds.length === 0;
    } else {
      isChecked = !excludedIds.some(
        (exId) => String(exId) === String(props.value),
      );
    }
  } else if (isAllGenericActive) {
    isChecked = true;
  }

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => null}
          style={{
            marginRight: "10px",
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "#A140E0",
          }}
        />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
};

const RadioOption = (props) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="radio"
          checked={props.isSelected}
          onChange={() => null}
          style={{
            marginRight: "10px",
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "#A140E0",
          }}
        />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CustomMenuList = (props) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.isLoadingUsers && (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            color: "#A140E0",
            fontSize: "14px",
            fontWeight: "bold",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          Loading more users...
        </div>
      )}
    </components.MenuList>
  );
};

const UserNotificationAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, type } = location.state ?? {};

  const ID = data?.id ?? "";

  const isViewMode = type === "view";
  const isEditMode = type === "edit";

  const [selectedUserOptions, setSelectedUserOptions] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [optionsList, setOptionsList] = useState([]);
  const [locationList, setLocationsList] = useState({
    states: [],
    cities: [],
    areas: [],
  });
  const admin_details = JSON.parse(
    localStorage.getItem("admin_details") ?? "{}",
  );

  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAllUsersSelected, setIsAllUsersSelected] = useState(false);
  const [excludedUserIds, setExcludedUserIds] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [allUserIds, setAllUserIds] = useState([]);

  const debounceTimer = useRef(null);
  const isFetchingRef = useRef(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger, // add this
  } = useForm({
    defaultValues: {
      bookingCount: 0,
      state: [{ value: "all", label: "All" }],
      city: [{ value: "all", label: "All" }],
      all_users: false,
      dob: "",
      users: [],
      customer: { value: 0, label: "All" },
      startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      notificationDate: "",
      notificationTime: "",
      createdBy: admin_details?.name || "Admin",
      service: { value: "all", label: "All" },
      description: "",
      selectAllState: false,
      selectAllCity: false,
      selectAllArea: false,
      selectAllUser: false,
    },
  });

  const selectedState = watch("state");
  const selectedCity = watch("city");
  const selectedCustomer = watch("customer");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const description = watch("description");
  const service_id = watch("service");
  const customer = watch("customer");
  const allUsersChecked = watch("all_users");

  const selectedUsersWatch = watch("users");
  const activeSelectedUserCount = isAllUsersSelected
    ? totalUserCount - excludedUserIds.length
    : Array.isArray(selectedUsersWatch)
      ? selectedUsersWatch.length
      : 0;

  // ✅ KEY CHANGE: When all_users is toggled ON, reset all filter fields to "All" defaults
  // and clear user selections. When toggled OFF, just unlock the fields.
  const prevAllUsersRef = useRef(false);
  useEffect(() => {
    if (allUsersChecked === prevAllUsersRef.current) return;
    prevAllUsersRef.current = allUsersChecked;

    if (allUsersChecked) {
      // Reset every filter field to its "All" / default value
      setValue("state", [{ value: "all", label: "All" }]);
      setValue("selectAllState", true);
      setValue("city", [{ value: "all", label: "All" }]);
      setValue("selectAllCity", true);
      setValue("customer", { value: 0, label: "All" });
      setValue("service", { value: "all", label: "All" });
      setValue("startDate", moment().subtract(1, "month").format("YYYY-MM-DD"));
      setValue("endDate", moment().format("YYYY-MM-DD"));

      // Clear user-specific state
      setValue("users", []);
      setIsAllUsersSelected(false);
      setExcludedUserIds([]);
      setSearchTerm("");
    }
    // When unchecked: do nothing — just let the fields become enabled again
    // with whatever values they currently have.
  }, [allUsersChecked, setValue]);

  const getLocationsList = useCallback(async (locationType, parent_id) => {
    try {
      const url = `${URLS.LOCATION}?type=${locationType}${
        parent_id ? "&parent_id=" + parent_id : ""
      }`;
      const res = await get(url);

      if (res?.status === "success") {
        const result = res.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        if (locationType === LOCATION_TYPES.STATE) {
          setLocationsList((prev) => ({ ...prev, states: result }));
        } else if (locationType === LOCATION_TYPES.CITY) {
          setLocationsList((prev) => ({ ...prev, cities: result }));
        } else if (locationType === LOCATION_TYPES.AREA) {
          setLocationsList((prev) => ({ ...prev, areas: result }));
        }
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, []);

  const getData = async () => {
    try {
      setIsInitializing(true);

      const res = await get(URLS.USERNOTIFICATION_DATA + data?.id);

      if (res?.status === "success") {
        const viewData = res.data;

        const selectedCustomerOption = customerOptions.find(
          (option) => option.value === viewData.notification_type,
        );
        setValue("customer", selectedCustomerOption || null);
        setValue("createdBy", viewData?.created_by);
        setValue(
          "startDate",
          viewData.from_date
            ? moment(viewData.from_date).format("YYYY-MM-DD")
            : moment().subtract(1, "month").format("YYYY-MM-DD"),
        );
        setValue(
          "endDate",
          viewData.to_date
            ? moment(viewData.to_date).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD"),
        );

        setValue("notificationDate", viewData.notification_date);
        setValue("notificationTime", viewData.notification_time);

        const userFilterPayload = {
          state_ids: viewData.states || [],
          city_ids: viewData.cities || [],
          area_ids: [],
          customer_type: viewData.notification_type || 0,
          from_date: viewData.from_date || null,
          to_date: viewData.to_date || null,
          search: "",
        };

        if (viewData.service?.id) {
          userFilterPayload.service_id = viewData.service.id;
        }

        const userRes = await post(
          URLS.USERNOTIFICATION_LIST,
          userFilterPayload,
        );

        if (userRes?.status === "success") {
          const totalCount = userRes.count || 0;
          const backendAllIds = userRes.all_user_ids || [];

          setTotalUserCount(totalCount);
          setAllUserIds(backendAllIds);
          setNextPageUrl(userRes.next);

          const paginatedOptions = (userRes.data || []).map((item) => ({
            value: item.id,
            label: `${item.name} (${item.code || "N/A"})`,
          }));
          setSelectedUserOptions(paginatedOptions);

          const savedUserIds = (viewData.users || []).map((user) =>
            String(user?.id || user),
          );

          if (savedUserIds.length > 0) {
            const exclusions = backendAllIds.filter(
              (id) => !savedUserIds.includes(String(id)),
            );

            setIsAllUsersSelected(true);
            setExcludedUserIds(exclusions);

            if (savedUserIds.length === totalCount) {
              setValue("users", [
                { value: "all", label: `All (${totalCount})` },
              ]);
            } else {
              setValue("users", [
                { value: "all", label: `Selected (${savedUserIds.length})` },
              ]);
            }
          } else {
            setValue("users", []);
          }
        }

        const isAllStatesSelected =
          locationList.states.length > 0 &&
          viewData.states.length === locationList.states.length;

        if (isAllStatesSelected) {
          setValue("state", [{ value: "all", label: "All" }]);
          setValue("selectAllState", true);

          const cityRes = await get(
            `${URLS.LOCATION}?type=${LOCATION_TYPES.CITY}`,
          );
          if (cityRes?.status === "success") {
            const allCities = cityRes.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            setLocationsList((prev) => ({ ...prev, cities: allCities }));

            const isAllCitiesSelected =
              viewData.cities.length === allCities.length;
            if (isAllCitiesSelected) {
              setValue("city", [{ value: "all", label: "All" }]);
              setValue("selectAllCity", true);
            } else {
              const mappedCities = allCities.filter((city) =>
                viewData.cities.includes(city.value),
              );
              setValue("city", mappedCities);
              setValue("selectAllCity", false);
            }
          }
        } else {
          const mappedStates = locationList.states.filter((state) =>
            viewData.states.includes(state.value),
          );
          setValue("state", mappedStates);

          let allCities = [];
          for (const stateId of viewData.states) {
            const cityRes = await get(
              `${URLS.LOCATION}?type=${LOCATION_TYPES.CITY}&parent_id=${stateId}`,
            );
            if (cityRes?.status === "success") {
              const cityOptions = cityRes.data.map((item) => ({
                value: item.id,
                label: item.name,
              }));
              allCities = [...allCities, ...cityOptions];
            }
          }

          setLocationsList((prev) => ({ ...prev, cities: allCities }));
          const mappedCities = allCities.filter((city) =>
            viewData.cities.includes(city.value),
          );
          setValue("city", mappedCities);
          setValue("selectAllCity", false);
        }

        setValue(
          "service",
          viewData.service
            ? [
                {
                  label: viewData.service.display_name,
                  value: viewData.service.id,
                },
              ]
            : { value: "all", label: "All" },
        );
        setValue("description", viewData.description);
        setValue("title", viewData.title);
        setValue("all_users", viewData.all_users);
        setValue("selected_users", viewData.selected_users);
      }

      setIsInitializing(false);
    } catch (error) {
      console.error("Error fetching notification data:", error);
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    setValue("startDate", moment().subtract(1, "month").format("YYYY-MM-DD"));
    setValue("endDate", moment().format("YYYY-MM-DD"));
  }, []);

  useEffect(() => {
    getLocationsList(LOCATION_TYPES.STATE);
  }, [getLocationsList]);

  useEffect(() => {
    if (data?.id && locationList.states.length > 0) {
      getData();
    }
  }, [data?.id, locationList.states]);

  useEffect(() => {
    const isAllSelected = Array.isArray(selectedState)
      ? selectedState[0]?.value === "all"
      : selectedState?.value === "all";

    const hasStates = Array.isArray(selectedState)
      ? selectedState.length > 0
      : !!selectedState;

    if (hasStates) {
      const fetchCities = async () => {
        const payload = {
          state_ids: isAllSelected
            ? []
            : Array.isArray(selectedState)
              ? selectedState.map((s) => s.value)
              : [selectedState.value],
        };

        try {
          const res = await post(
            URLS.NOTIFICATION_LOCATION_CITIES_BY_STATES,
            payload,
          );

          if (res?.status === "success") {
            const allCities = res.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            setLocationsList((prev) => ({ ...prev, cities: allCities }));

            const currentCities = getValues("city");

            if (!currentCities || currentCities.length === 0) {
              setValue("selectAllCity", true);
              setValue("city", [{ value: "all", label: "All" }]);
            } else {
              if (currentCities[0]?.value !== "all") {
                const validCities = currentCities.filter((selected) =>
                  allCities.some((valid) => valid.value === selected.value),
                );

                if (validCities.length === 0) {
                  setValue("selectAllCity", true);
                  setValue("city", [{ value: "all", label: "All" }]);
                } else {
                  setValue("city", validCities);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      };

      fetchCities();
    } else {
      setLocationsList((prev) => ({ ...prev, cities: [], areas: [] }));
      setValue("city", []);
      setValue("area", []);
    }
  }, [selectedState, setValue, getValues]);

  const onSubmit = async (data) => {
    const getStateIds = () => {
      if (!data?.state?.length) return [];
      if (data.state[0]?.value === "all") {
        return locationList?.states?.map((s) => s.value) || [];
      }
      return data.state.map((s) => s.value);
    };

    const getCityIds = () => {
      if (!data?.city?.length) return [];
      if (data.city[0]?.value === "all") {
        return locationList?.cities?.map((c) => c.value) || [];
      }
      return data.city.map((c) => c.value);
    };

    const getFinalUserIds = () => {
      if (isAllUsersSelected) {
        const stringExclusions = excludedUserIds.map((id) => String(id));
        return allUserIds.filter(
          (id) => !stringExclusions.includes(String(id)),
        );
      }
      if (!data?.users) return [];
      return Array.isArray(data.users)
        ? data.users.map((item) => item?.value).filter(Boolean)
        : data.users?.value
          ? [data.users.value]
          : [];
    };

    const payload = {
      notification_type: data?.customer?.value ?? 0,
      state_ids: getStateIds?.() ?? [],
      city_ids: getCityIds?.() ?? [],
      user_ids: getFinalUserIds(),
      from_date: data?.startDate ?? null,
      to_date: data?.endDate ?? null,
      description: data?.description ?? "",
      title: data?.title ?? "",
      service_id:
        service_id?.value && service_id.value !== "all" ? service_id.value : 0,
      all_users: data?.all_users,
      selected_users: customer?.value !== 0 ? true : false,
      notification_date: moment(data.notificationDate).format("YYYY-MM-DD"),
      notification_time: data.notificationTime,
    };

    // console.log("payload--------->", payload);
    // return;

    try {
      const url = isEditMode
        ? `${URLS.USERNOTIFICATION_ADD}${ID}/`
        : URLS.USERNOTIFICATION_ADD;

      const method = isEditMode ? put : post;
      const res = await method(url, payload);

      if (res?.status === "success") {
        toast.success(res?.message || "Notification saved successfully");
        navigate("/user-list");
      } else {
        toast.error(res?.message || "Failed to save notification");
      }
    } catch (error) {
      console.error("Error saving notification:", error);
      toast.error("An error occurred while saving the notification");
    }
  };

  const handlerExcelDownload = async () => {
    const formData = getValues();

    const getStateIds = () => {
      if (!formData?.state?.length) return [];
      if (formData.state[0]?.value === "all") {
        return locationList?.states?.map((s) => s.value) || [];
      }
      return formData.state.map((s) => s.value);
    };

    const getCityIds = () => {
      if (!formData?.city?.length) return [];
      if (formData.city[0]?.value === "all") {
        return locationList?.cities?.map((c) => c.value) || [];
      }
      return formData.city.map((c) => c.value);
    };

    const getFinalUserIds = () => {
      if (isAllUsersSelected) {
        const stringExclusions = excludedUserIds.map((id) => String(id));
        return allUserIds.filter(
          (id) => !stringExclusions.includes(String(id)),
        );
      }
      if (!formData?.users) return [];
      return Array.isArray(formData.users)
        ? formData.users.map((item) => item?.value).filter(Boolean)
        : formData.users?.value
          ? [formData.users.value]
          : [];
    };

    const payload = {
      notification_type: formData?.customer?.value || 0,
      state_ids: getStateIds(),
      city_ids: getCityIds(),
      user_ids: getFinalUserIds(),
      from_date: formData?.startDate || null,
      to_date: formData?.endDate || null,
      service_id:
        formData?.service?.value && formData.service.value !== "all"
          ? formData.service.value
          : 0,
      description: formData?.description || "",
      title: formData?.title || "",
    };

    try {
      const url = URLS.EXCEL_DOWNLOAD_NOTIFICATION;
      let res = await HttpClient.post(url, payload, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "bookingList.xlsx";
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#F4F4F4",
      borderRadius: 8,
      border: "none",
      padding: "8px",
      width: "100%",
      boxShadow: "none",
      minHeight: "45px",
      pointerEvents: "auto",
      cursor: state?.isDisabled ? "not-allowed" : "pointer",
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      display: state?.isDisabled ? "none" : "flex",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      cursor: state?.isDisabled ? "not-allowed" : "default",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-track": {
        background: "#F4F4F4",
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#c1c1c1",
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb:hover": { background: "#a8a8a8" },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#e9ecef" : "transparent",
      color: "#000",
      cursor: "pointer",
      "&:active": { backgroundColor: "#e9ecef" },
    }),
  };

  const hideMultiValue = { ...customStyles };

  const customerOptions = [
    { value: 0, label: "All" },
    { value: 1, label: "Birth Date" },
    { value: 2, label: "Booked" },
    { value: 3, label: "Un Booked" },
    { value: 4, label: "Cancellation" },
  ];

  const fetchUserList = useCallback(
    async (pageUrl = null, searchString = "") => {
      if (isInitializing && data?.id) return;
      if (isFetchingRef.current) return;

      const getStateIds = () => {
        if (
          !selectedState ||
          (Array.isArray(selectedState) && selectedState.length === 0)
        )
          return [];
        const states = Array.isArray(selectedState)
          ? selectedState
          : [selectedState];
        if (states[0]?.value === "all") {
          return locationList.states.map((s) => s.value);
        }
        return states.map((s) => s.value);
      };

      const getCityIds = () => {
        if (
          !selectedCity ||
          (Array.isArray(selectedCity) && selectedCity.length === 0)
        )
          return [];
        const cities = Array.isArray(selectedCity)
          ? selectedCity
          : [selectedCity];
        if (cities[0]?.value === "all") {
          return locationList.cities.map((c) => c.value);
        }
        return cities.map((c) => c.value);
      };

      const requiresDates =
        selectedCustomer?.value && selectedCustomer.value > 0;
      if (requiresDates && (!startDate || !endDate)) {
        setSelectedUserOptions([]);
        return;
      }

      const payload = {
        state_ids: getStateIds(),
        city_ids: getCityIds(),
        area_ids: [],
        customer_type: selectedCustomer?.value || 0,
        from_date: startDate || null,
        to_date: endDate || null,
        search: searchString,
      };
      if (service_id?.value !== "all") {
        payload.service_id = service_id?.value;
      }

      if (
        payload.state_ids.length === 0 &&
        payload.city_ids.length === 0 &&
        payload.customer_type === 0 &&
        !payload.from_date &&
        !payload.to_date
      ) {
        setSelectedUserOptions([]);
        return;
      }

      try {
        isFetchingRef.current = true;
        setIsLoadingUsers(true);

        let endpoint = URLS.USERNOTIFICATION_LIST;
        if (pageUrl) {
          endpoint = pageUrl.replace(/^.*\/\/[^\/]+/, "");
        }

        const res = await post(endpoint, payload);

        if (res?.status === "success" && Array.isArray(res.data)) {
          const isSearching = searchString && searchString.trim().length > 0;

          if (!isSearching) {
            setValue("bookingCount", res.booking_count || 0);
            setValue("userCount", res.count || 0);
            setTotalUserCount(res.count || 0);
            setAllUserIds(res.all_user_ids || []);
          }

          setNextPageUrl(res.next);

          const result = res.data.map((item) => ({
            label: `${item.name} (${item.code || "N/A"})`,
            value: item.id,
          }));

          if (pageUrl) {
            setSelectedUserOptions((prev) => [...prev, ...result]);
          } else {
            setSelectedUserOptions(result);

            const currentUsers = getValues("users");
            if (!isSearching && (!currentUsers || currentUsers.length === 0)) {
              if (res.count && res.count > 0) {
                setIsAllUsersSelected(true);
                setExcludedUserIds([]);
                setValue("users", [
                  { value: "all", label: `All (${res.count})` },
                ]);
              } else {
                setIsAllUsersSelected(false);
                setExcludedUserIds([]);
                setValue("users", []);
              }
            }
          }
        } else {
          if (!pageUrl) setSelectedUserOptions([]);

          const isSearching = searchString && searchString.trim().length > 0;
          if (!isSearching) {
            setValue("bookingCount", 0);
            setValue("userCount", 0);
            setTotalUserCount(0);
            setAllUserIds([]);
          }
        }
      } catch (error) {
        console.error("Error fetching filtered users:", error);
        toast.error(error || "Failed to fetch users");
      } finally {
        isFetchingRef.current = false;
        setIsLoadingUsers(false);
      }
    },
    [
      selectedState,
      selectedCity,
      selectedCustomer,
      startDate,
      endDate,
      locationList,
      data,
      isInitializing,
      setValue,
      service_id,
    ],
  );

  const handleUserSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      setSearchTerm(inputValue);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchUserList(null, inputValue);
      }, 500);
    }
  };

  const getServices = async () => {
    let res = await get(`${URLS.SERVICESLIST}list`);
    let formatted = res?.data?.map((item) => ({
      label: item.display_name,
      value: item.id,
    }));
    setOptionsList(formatted);
  };

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  useEffect(() => {
    getServices();
  }, []);

  useEffect(() => {
    if (!isInitializing && data?.id) {
      fetchUserList();
    }
  }, [isInitializing, data?.id, fetchUserList]);

  const resetDate = () => {
    setValue("startDate", "");
    setValue("endDate", "");
  };

  const resetUsersField = () => {
    setValue("users", []);
    setIsAllUsersSelected(false);
    setExcludedUserIds([]);
  };

  const isAllStatesActive = Array.isArray(selectedState)
    ? selectedState.some((s) => s.value === "all")
    : false;
  const isAllCitiesActive = Array.isArray(selectedCity)
    ? selectedCity.some((c) => c.value === "all")
    : false;

  const isServiceAllSelected = customer?.value === 0;
  return (
    <div className="container-fluid mb-5">
      <Topbar header={"Notification User"} back={true} />
      <div className="mt-4 px-3">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Controller
              name="all_users"
              control={control}
              render={({ field }) => (
                <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    style={{
                      cursor: "pointer",
                      width: 18,
                      height: 18,
                      accentColor: "#A140E0",
                    }}
                  />
                  <p style={{ fontSize: 16, fontWeight: 800 }}>All Users</p>
                </div>
              )}
            />
          </div>
          <div className="row">
            {/* Column 1: Left */}
            <div className="col-md-6">
              {/* State */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="f2 fs-16 m-0">State</label>
                </div>
                <Controller
                  name="state"
                  control={control}
                  rules={{
                    required: "State is required",
                    validate: (value) =>
                      (Array.isArray(value) && value.length > 0) ||
                      "Please select at least one state",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isAllSelected={isAllStatesActive}
                      onChange={(value, actionMeta) => {
                        const { action, option, removedValue } = actionMeta;

                        if (
                          action === "select-option" &&
                          option?.value === "all"
                        ) {
                          setValue("selectAllState", true);
                          field.onChange([{ value: "all", label: "All" }]);
                          resetUsersField();
                          return;
                        }

                        if (
                          action === "clear" ||
                          (action === "remove-value" &&
                            removedValue?.value === "all") ||
                          !value ||
                          value.length === 0
                        ) {
                          setValue("selectAllState", false);
                          field.onChange([]);
                          resetUsersField();
                          return;
                        }

                        if (
                          isAllStatesActive &&
                          action === "select-option" &&
                          option
                        ) {
                          setValue("selectAllState", false);
                          const allOtherStates = locationList.states.filter(
                            (s) => String(s.value) !== String(option.value),
                          );
                          field.onChange(allOtherStates);
                          resetUsersField();
                          return;
                        }

                        setValue("selectAllState", false);
                        field.onChange(value);
                        resetUsersField();
                      }}
                      options={[
                        { value: "all", label: "All" },
                        ...locationList.states,
                      ]}
                      placeholder="Select State"
                      isMulti
                      isClearable={true}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isDisabled={isViewMode || allUsersChecked}
                      styles={{
                        ...hideMultiValue,
                        control: (provided, state) => ({
                          ...hideMultiValue.control(provided, state),
                          border: errors.state ? "1px solid red" : "none",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          maxHeight: "140px",
                          overflowY: "auto",
                          "&::-webkit-scrollbar": { width: "6px" },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c1c1c1",
                            borderRadius: "8px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            background: "#a8a8a8",
                          },
                        }),
                      }}
                      components={{ Option: CheckboxOption }}
                    />
                  )}
                />
                {errors.state && (
                  <span className="text-danger fs-12">
                    {errors.state.message}
                  </span>
                )}
              </div>

              {/* Customer */}
              <div className="mb-4">
                <label className="f2 fs-16 mb-2 d-block">Status</label>
                <Controller
                  name="customer"
                  control={control}
                  render={({ field }) => (
                    <div
                      style={{
                        border: allUsersChecked ? "" : "2px solid #A140E0",
                        borderRadius: 7,
                      }}
                    >
                      <Select
                        {...field}
                        onChange={(val) => {
                          resetUsersField();
                          field.onChange(val);
                        }}
                        options={customerOptions}
                        placeholder="Select Customer Status"
                        styles={customStyles}
                        isDisabled={isViewMode || allUsersChecked}
                        components={{ Option: RadioOption }}
                      />
                    </div>
                  )}
                />
              </div>

              {/* Start Date */}
              <div className="mb-4">
                <div
                  className="d-flex mb-2"
                  style={{ alignItems: "center", gap: 10 }}
                >
                  <label className="f2 fs-18 d-block">Start Date</label>
                  <div
                    style={{
                      borderRadius: "12px",
                      backgroundColor: allUsersChecked ? "#adadad" : "#A140E0",
                      paddingTop: 5,
                      paddingBottom: 5,
                      paddingLeft: 20,
                      paddingRight: 20,
                      color: "#fff",
                      cursor: "pointer",
                    }}
                    onClick={() => (allUsersChecked ? null : resetDate())}
                  >
                    <label className="f2 fs-14 d-block">Reset Date</label>
                  </div>
                </div>
                <div
                  className="d-flex align-items-center px-3"
                  style={{
                    backgroundColor: "#F4F4F4",
                    borderRadius: "8px",
                    height: "55px",
                    border: errors.startDate ? "1px solid red" : "none",
                  }}
                >
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      required:
                        selectedCustomer?.value && selectedCustomer.value > 0
                          ? "Start Date is required"
                          : false,
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className="border-0 bg-transparent w-100 h-100"
                        max={
                          endDate
                            ? moment(endDate).format("YYYY-MM-DD")
                            : undefined
                        }
                        value={
                          field.value
                            ? moment(field.value, [
                                "YYYY-MM-DD",
                                "DD-MM-YYYY",
                              ]).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) => {
                          resetUsersField();
                          field.onChange(e.target.value);
                        }}
                        disabled={isViewMode || allUsersChecked}
                      />
                    )}
                  />
                </div>
                {errors.startDate && (
                  <span className="text-danger fs-12">
                    {errors.startDate.message}
                  </span>
                )}
              </div>

              <div className="row mb-4">
                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">User Count</label>
                  <div style={{ position: "relative" }}>
                    <Controller
                      name="userCount"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          placeholder="0"
                          style={{
                            backgroundColor: "#F4F4F4",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "15px",
                            width: "100%",
                            height: "55px",
                            padding: "0 10px",
                          }}
                          disabled={true}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">Booking Count</label>
                  <div style={{ position: "relative" }}>
                    <Controller
                      name="bookingCount"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          placeholder="0"
                          style={{
                            backgroundColor: "#F4F4F4",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "15px",
                            width: "100%",
                            height: "55px",
                            padding: "0 10px",
                          }}
                          disabled={true}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="mb-4">
                <label className="f2 fs-16 mb-2 d-block">Title</label>
                <div style={{ position: "relative" }}>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: "Title is required" }}
                    render={({ field }) => (
                      <input
                        {...field}
                        placeholder="text.."
                        rows="8"
                        maxLength={1000}
                        style={{
                          backgroundColor: "#F4F4F4",
                          border: errors.title
                            ? "2px solid red"
                            : allUsersChecked
                              ? "2px solid #A140E0"
                              : "none",
                          borderRadius: "8px",
                          resize: "none",
                          fontSize: "15px",
                          width: "100%",
                          height: "55px",
                        }}
                        disabled={isViewMode}
                      />
                    )}
                  />
                </div>
                {errors.title && (
                  <span className="text-danger fs-12">
                    {errors.title.message}
                  </span>
                )}
              </div>

              {/* <div className="row mb-4">
                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">
                    Notification Date
                  </label>
                  <div
                    className="d-flex align-items-center px-3"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      border: errors.notificationDate
                        ? "1px solid red"
                        : "none",
                    }}
                  >
                    <Controller
                      name="notificationDate"
                      control={control}
                      rules={{
                        required: "Notification Date is required",
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="date"
                          className="border-0 bg-transparent w-100 h-100"
                          disabled={isViewMode}
                          value={field.value || ""}
                        />
                      )}
                    />
                  </div>
                  {errors.notificationDate && (
                    <span className="text-danger">
                      {errors.notificationDate.message}
                    </span>
                  )}
                </div>

                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">
                    Notification Time
                  </label>
                  <div
                    className="d-flex align-items-center px-3"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      border: errors.notificationTime
                        ? "1px solid red"
                        : "none",
                    }}
                  >
                    <Controller
                      name="notificationTime"
                      control={control}
                      rules={{
                        required: "Notification Time is required",
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="time"
                          className="border-0 bg-transparent w-100 h-100"
                          disabled={isViewMode}
                          value={field.value || ""}
                        />
                      )}
                    />
                  </div>
                  {errors.notificationTime && (
                    <span className="text-danger">
                      {errors.notificationTime.message}
                    </span>
                  )}
                </div>
              </div> */}
              <div className="row mb-4">
                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">
                    Notification Date
                  </label>
                  <div
                    className="d-flex align-items-center px-3"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      border: errors.notificationDate
                        ? "1px solid red"
                        : "none",
                    }}
                  >
                    <Controller
                      name="notificationDate"
                      control={control}
                      rules={{
                        required: "Notification Date is required",
                        validate: (value) => {
                          if (!value) return "Notification Date is required";
                          const today = moment().startOf("day");
                          const selected = moment(value, "YYYY-MM-DD").startOf(
                            "day",
                          );
                          if (selected.isBefore(today)) {
                            return "Past date is not allowed";
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="date"
                          className="border-0 bg-transparent w-100 h-100"
                          min={moment().format("YYYY-MM-DD")}
                          disabled={isViewMode}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            // Re-validate time whenever date changes
                            trigger && trigger("notificationTime");
                          }}
                        />
                      )}
                    />
                  </div>
                  {errors.notificationDate && (
                    <span className="text-danger">
                      {errors.notificationDate.message}
                    </span>
                  )}
                </div>

                <div className="col-6">
                  <label className="f2 fs-16 mb-2 d-block">
                    Notification Time
                  </label>
                  <div
                    className="d-flex align-items-center px-3"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      border: errors.notificationTime
                        ? "1px solid red"
                        : "none",
                    }}
                  >
                    <Controller
                      name="notificationTime"
                      control={control}
                      rules={{
                        required: "Notification Time is required",
                        validate: (value) => {
                          if (!value) return "Notification Time is required";

                          const selectedDate = getValues("notificationDate");
                          const today = moment().format("YYYY-MM-DD");

                          // Only restrict time if the selected date is today
                          if (
                            selectedDate &&
                            moment(selectedDate).format("YYYY-MM-DD") === today
                          ) {
                            const now = moment();
                            const [hh, mm] = value.split(":");
                            const selectedTime = moment().set({
                              hour: parseInt(hh, 10),
                              minute: parseInt(mm, 10),
                              second: 0,
                              millisecond: 0,
                            });

                            if (selectedTime.isSameOrBefore(now)) {
                              return "Please select a future time";
                            }
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="time"
                          className="border-0 bg-transparent w-100 h-100"
                          disabled={isViewMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </div>
                  {errors.notificationTime && (
                    <span className="text-danger">
                      {errors.notificationTime.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Column 2: Right */}
            <div className="col-md-6">
              {/* City */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="f2 fs-16 m-0">City</label>
                </div>
                <Controller
                  name="city"
                  control={control}
                  rules={{
                    required: "City is required",
                    validate: (value) =>
                      (Array.isArray(value) && value.length > 0) ||
                      "Please select at least one city",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isAllSelected={isAllCitiesActive}
                      onChange={(value, actionMeta) => {
                        const { action, option, removedValue } = actionMeta;

                        if (
                          action === "select-option" &&
                          option?.value === "all"
                        ) {
                          setValue("selectAllCity", true);
                          field.onChange([{ value: "all", label: "All" }]);
                          resetUsersField();
                          return;
                        }

                        if (
                          action === "clear" ||
                          (action === "remove-value" &&
                            removedValue?.value === "all") ||
                          !value ||
                          value.length === 0
                        ) {
                          setValue("selectAllCity", false);
                          field.onChange([]);
                          resetUsersField();
                          return;
                        }

                        if (
                          isAllCitiesActive &&
                          action === "select-option" &&
                          option
                        ) {
                          setValue("selectAllCity", false);
                          const allOtherCities = locationList.cities.filter(
                            (c) => String(c.value) !== String(option.value),
                          );
                          field.onChange(allOtherCities);
                          resetUsersField();
                          return;
                        }

                        setValue("selectAllCity", false);
                        field.onChange(value);
                        resetUsersField();
                      }}
                      options={[
                        { value: "all", label: "All" },
                        ...locationList.cities,
                      ]}
                      placeholder="Select City"
                      isMulti
                      isClearable={true}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isDisabled={
                        !selectedState ||
                        selectedState.length === 0 ||
                        isViewMode ||
                        allUsersChecked
                      }
                      styles={{
                        ...hideMultiValue,
                        control: (provided, state) => ({
                          ...hideMultiValue.control(provided, state),
                          border: errors.city ? "1px solid red" : "none",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          maxHeight: "140px",
                          overflowY: "auto",
                          "&::-webkit-scrollbar": { width: "6px" },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c1c1c1",
                            borderRadius: "8px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            background: "#a8a8a8",
                          },
                        }),
                      }}
                      components={{ Option: CheckboxOption }}
                    />
                  )}
                />
                {errors.city && (
                  <span className="text-danger fs-12">
                    {errors.city.message}
                  </span>
                )}
              </div>

              {/* Service */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="f2 fs-16 m-0">
                    Service Type{" "}
                    <span className="f2 fs-12 m-0">(optional)</span>
                  </label>
                </div>
                <Controller
                  name="service"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[{ value: "all", label: "All" }, ...optionsList]}
                      placeholder="Select Service"
                      styles={{ ...hideMultiValue }}
                      isDisabled={isViewMode || allUsersChecked}
                      onChange={(val) => {
                        resetUsersField();
                        field.onChange(val);
                      }}
                      components={{ Option: RadioOption }}
                    />
                  )}
                />
              </div>

              {/* End Date */}
              <div className="mb-4 pt-1">
                <label className="f2 fs-16 mb-2 d-block">End Date</label>
                <div
                  className="d-flex align-items-center px-3"
                  style={{
                    backgroundColor: "#F4F4F4",
                    borderRadius: "8px",
                    height: "55px",
                    border: errors.endDate ? "1px solid red" : "none",
                  }}
                >
                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      required:
                        selectedCustomer?.value && selectedCustomer.value > 0
                          ? "End Date is required"
                          : false,
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className="border-0 bg-transparent w-100 h-100"
                        min={
                          startDate
                            ? moment(startDate).format("YYYY-MM-DD")
                            : undefined
                        }
                        value={
                          field.value
                            ? moment(field.value, [
                                "YYYY-MM-DD",
                                "DD-MM-YYYY",
                              ]).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) => {
                          resetUsersField();
                          field.onChange(e.target.value);
                        }}
                        disabled={isViewMode || allUsersChecked}
                      />
                    )}
                  />
                </div>
                {errors.endDate && (
                  <span className="text-danger fs-12">
                    {errors.endDate.message}
                  </span>
                )}
              </div>

              {/* User */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="f2 fs-16 m-0">User</label>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "15px" }}
                  >
                    {searchTerm && (
                      <span
                        onClick={() => {
                          setSearchTerm("");
                          fetchUserList(null, "");
                        }}
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontSize: "13px",
                          fontWeight: "bold",
                          textDecoration: "underline",
                        }}
                      >
                        Clear Search
                      </span>
                    )}
                    <span
                      className="f2 fs-14"
                      style={{ color: "#A140E0", fontWeight: "bold" }}
                    >
                      Selected: {activeSelectedUserCount}
                    </span>
                  </div>
                </div>
                <Controller
                  name="users"
                  control={control}
                  rules={{
                    required: "User is required",
                    validate: (value) =>
                      (Array.isArray(value) && value.length > 0) ||
                      "Please select at least one user",
                  }}
                  render={({ field }) => {
                    const currentSelections = Array.isArray(field.value)
                      ? field.value
                      : [];
                    const individualSelections = currentSelections.filter(
                      (c) => c.value !== "all",
                    );
                    const mergedOptions = [
                      ...individualSelections,
                      ...selectedUserOptions,
                    ];
                    const uniqueOptions = mergedOptions.filter(
                      (v, i, a) =>
                        a.findIndex((t) => t.value === v.value) === i,
                    );
                    const allDropdownLabel = `All (${totalUserCount})`;

                    return (
                      <div
                        style={{
                          border: allUsersChecked ? "" : "2px solid #A140E0",
                          borderRadius: 7,
                        }}
                      >
                        <Select
                          {...field}
                          options={
                            totalUserCount > 0
                              ? [
                                  { value: "all", label: allDropdownLabel },
                                  ...uniqueOptions,
                                ]
                              : uniqueOptions
                          }
                          inputValue={searchTerm}
                          onChange={(value, actionMeta) => {
                            const { action, option, removedValue } = actionMeta;

                            if (
                              action === "select-option" &&
                              option?.value === "all" &&
                              isServiceAllSelected
                            ) {
                              toast.warning(
                                "Cannot select 'All' users when Service is set to 'All'",
                              );
                              return;
                            }

                            if (
                              action === "select-option" &&
                              option?.value === "all"
                            ) {
                              setIsAllUsersSelected(true);
                              setExcludedUserIds([]);
                              field.onChange([
                                {
                                  value: "all",
                                  label: `All (${totalUserCount})`,
                                },
                              ]);
                              return;
                            }

                            if (
                              action === "clear" ||
                              (action === "remove-value" &&
                                removedValue?.value === "all") ||
                              !value ||
                              value.length === 0
                            ) {
                              setIsAllUsersSelected(false);
                              setExcludedUserIds([]);
                              field.onChange([]);
                              return;
                            }

                            if (isAllUsersSelected) {
                              const removedItem =
                                removedValue ||
                                (action === "deselect-option" ? option : null);
                              if (removedItem?.value === "all") {
                                setIsAllUsersSelected(false);
                                setExcludedUserIds([]);
                                field.onChange([]);
                                return;
                              }

                              if (action === "select-option" && option) {
                                const clickedId = String(option.value);
                                let newExcluded = [...excludedUserIds].map(
                                  (id) => String(id),
                                );

                                if (newExcluded.includes(clickedId)) {
                                  newExcluded = newExcluded.filter(
                                    (id) => id !== clickedId,
                                  );
                                } else {
                                  newExcluded.push(clickedId);
                                }

                                if (
                                  newExcluded.length === totalUserCount &&
                                  totalUserCount > 0
                                ) {
                                  setIsAllUsersSelected(false);
                                  setExcludedUserIds([]);
                                  field.onChange([]);
                                  return;
                                }

                                setExcludedUserIds(newExcluded);
                                const currentCount =
                                  totalUserCount - newExcluded.length;
                                const newLabel =
                                  newExcluded.length === 0
                                    ? `All (${totalUserCount})`
                                    : `Selected (${currentCount})`;
                                field.onChange([
                                  { value: "all", label: newLabel },
                                ]);
                              }
                            } else {
                              setIsAllUsersSelected(false);
                              setExcludedUserIds([]);
                              field.onChange(value);
                            }
                            setSearchTerm("");
                            fetchUserList(null, "");
                          }}
                          onInputChange={handleUserSearch}
                          isLoadingUsers={isLoadingUsers}
                          onMenuScrollToBottom={() => {
                            if (nextPageUrl && !isFetchingRef.current) {
                              fetchUserList(nextPageUrl, searchTerm);
                            }
                          }}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          isAllUsersSelected={isAllUsersSelected}
                          excludedUserIds={excludedUserIds}
                          placeholder="Select User"
                          isMulti
                          isClearable={true}
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          isDisabled={
                            isViewMode ||
                            allUsersChecked ||
                            isServiceAllSelected
                          }
                          styles={{
                            ...hideMultiValue,
                            control: (provided, state) => ({
                              ...hideMultiValue.control(provided, state),
                              border: errors.users ? "1px solid red" : "none",
                            }),
                            valueContainer: (provided) => ({
                              ...provided,
                              maxHeight: "140px",
                              overflowY: "auto",
                              "&::-webkit-scrollbar": { width: "6px" },
                              "&::-webkit-scrollbar-track": {
                                background: "transparent",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                background: "#c1c1c1",
                                borderRadius: "8px",
                              },
                              "&::-webkit-scrollbar-thumb:hover": {
                                background: "#a8a8a8",
                              },
                            }),
                          }}
                          components={{
                            Option: CheckboxOption,
                            MenuList: CustomMenuList,
                          }}
                        />
                      </div>
                    );
                  }}
                />
                {errors.users && (
                  <span className="text-danger fs-12">
                    {errors.users.message}
                  </span>
                )}
              </div>

              {/* Created By */}
              <div className="mb-4">
                <label className="f2 fs-16 mb-2 d-block">Created By</label>
                <Controller
                  name="createdBy"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      readOnly
                      style={{
                        backgroundColor: "#F4F4F4",
                        border: "none",
                        padding: "12px",
                        borderRadius: "8px",
                        height: "55px",
                        width: "100%",
                      }}
                    />
                  )}
                />
              </div>
            </div>

            {/* Description */}
            <div className="col-12 mb-4">
              <label className="f2 fs-16 mb-2 d-block">Description</label>
              <div style={{ position: "relative" }}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: "Description is required" }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      placeholder="text.."
                      rows="8"
                      maxLength={1000}
                      style={{
                        backgroundColor: "#F4F4F4",
                        border: errors.description
                          ? "2px solid red"
                          : allUsersChecked
                            ? "2px solid #A140E0"
                            : "none",
                        borderRadius: "8px",
                        resize: "none",
                        fontSize: "15px",
                        width: "100%",
                      }}
                      disabled={isViewMode}
                    />
                  )}
                />
                <div
                  className="f1 fs-12 text-muted"
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    right: "15px",
                  }}
                >
                  {description?.length || 0}/1000 words
                </div>
              </div>
              {errors.description && (
                <span className="text-danger fs-12">
                  {errors.description.message}
                </span>
              )}
            </div>

            {/* Submit Button */}
            {!isViewMode && (
              <div className="col-12 d-flex justify-content-between mt-4">
                <button
                  type="button"
                  className="white f2 py-3 px-5 border-0"
                  style={{
                    borderRadius: "12px",
                    backgroundColor: "#8f58c7f0",
                    minWidth: "250px",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  onClick={() => handlerExcelDownload()}
                >
                  Excel Download
                </button>
                <button
                  type="submit"
                  className="white f2 py-3 px-5 border-0"
                  style={{
                    borderRadius: "12px",
                    backgroundColor: "#A140E0",
                    minWidth: "250px",
                    fontSize: "18px",
                  }}
                >
                  {isEditMode ? "Submit" : "Save"}
                </button>
              </div>
            )}
          </div>
        </form>

        <div className="col-12 d-flex justify-content-end mt-4">
          {isViewMode && (
            <button onClick={() => navigate(-1)} className="back-btn mt-3">
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotificationAdd;
